/**
 * The Client create / edit dialog (f6.1).
 *
 * Built through f2.2's `Modal` on the prototype's own `modal-scrim` /
 * `modal-head` / `modal-body` / `modal-foot` shapes, and reproducing **three
 * of the prototype's four fieldsets**: `Identity`, `Feed matching` and
 * `Status`.
 *
 * **Deliberate departures**, each argued rather than silent:
 *
 * | Element | What is built | Why |
 * | --- | --- | --- |
 * | The `Ownership` fieldset, its **required** `Delivery Manager (DM)` select and its `Assignment valid from` date | **Absent** | Ownership is a `DeliveryRoleAssignment` with its own lifecycle, its own audit entries and its own history. A required create-form field would reintroduce the `dm_id` column this feature refuses, and on a fresh installation there may be no assignable account at all. A DM is named from the detail screen, exactly as a DEM, a CEM and a CM are. f4.2 set the rule and f5.1 already applied it to CM |
 * | A Business Unit select on the EDIT dialog | Read-only on edit | The parent is half the uniqueness key, so a move either collides with a code in the target unit or silently frees one in the source -- and once f9.1 ships, re-parenting is a bulk permission change wearing the costume of a dropdown. Deferred, not forbidden: it would arrive as its own transition route with its own audit action |
 * | `Reporting currency` | **Absent** | Inherited from the parent unit and read-only. There is no column: `OR_Q_July 22` column K is a clean 100 per cent split by unit, so a client reports in its unit's currency and never restates it |
 * | The `Status` select on CREATE | Kept, and offered on create only | The prototype draws it, f2.2's catalogue supplies the values, and the server defaults to `ACTIVE` when it is omitted. On EDIT it is absent: the transitions are the row actions, where the child guard and the static audit action live, and a select here would record a deactivation as an ordinary edit |
 *
 * **No control holds a map from code to label.** The status select is
 * populated from `GET /lookups`, so a status renamed in the lookup renames
 * itself here.
 *
 * The SAP ID hint names the field it comes from -- Kimble's `Account: SAPID` --
 * and says plainly that leaving it blank is normal, because the master
 * workbook carries no such column and f10.4 fills them in from the feed.
 */

import { useMemo, useState } from 'react';
import { Check } from 'lucide-react';

import type { BusinessUnit, Client } from '@/api/client';
import { useAddClient, useLookupCatalog, useUpdateClient } from '@/api/queries';
import { DialogField, Modal } from '@/routes/reference-data/Modal';

import { generalMessage, linesToAliases, problemFor } from './clientView';

/** The lookup type this dialog's status select reads. Values are the server's. */
const ENTITY_STATUS_TYPE = 'ENTITY_STATUS';

export function ClientDialog({
  client,
  units,
  onClose,
}: {
  /** null for a create. */
  client: Client | null;
  /** The ACTIVE units a create may name. An inactive one is refused server-side. */
  units: readonly BusinessUnit[];
  onClose: () => void;
}) {
  const editing = client !== null;
  const create = useAddClient();
  const update = useUpdateClient();
  const catalog = useLookupCatalog();

  const [unitId, setUnitId] = useState(
    client?.business_unit.id ?? units[0]?.id ?? '',
  );
  const [code, setCode] = useState(client?.code ?? '');
  const [name, setName] = useState(client?.name ?? '');
  const [sapId, setSapId] = useState(client?.sap_id ?? '');
  const [aliasText, setAliasText] = useState((client?.aliases ?? []).join('\n'));
  const [statusId, setStatusId] = useState('');

  const statuses = useMemo(
    () =>
      (
        catalog.data?.types.find((type) => type.code === ENTITY_STATUS_TYPE)?.values ??
        []
      ).filter(
        /*
         * Only active values are offered for a NEW record; the list and detail
         * screens always render the STORED value, active or not. f2.2's
         * consumer rule, applied without restatement.
         */
        (value) => value.is_active,
      ),
    [catalog.data],
  );

  const failure = create.error ?? update.error;
  const busy = create.isPending || update.isPending;

  function submit() {
    const aliases = linesToAliases(aliasText);
    const trimmedSap = sapId.trim();

    if (editing) {
      update.mutate(
        {
          clientId: client.id,
          change: {
            name,
            /*
             * An empty field CLEARS the SAP ID, which the server distinguishes
             * from an absent one. A wrong SAP ID resolves a feed to the wrong
             * client, so removing it must not require deactivating the client.
             */
            sap_id: trimmedSap === '' ? null : trimmedSap,
            aliases,
          },
        },
        { onSuccess: onClose },
      );
      return;
    }

    create.mutate(
      {
        business_unit_id: unitId,
        code,
        name,
        ...(trimmedSap ? { sap_id: trimmedSap } : {}),
        ...(aliases.length ? { aliases } : {}),
        /*
         * Omitted rather than guessed: the server defaults to ACTIVE, and no
         * status literal exists anywhere in this application.
         */
        ...(statusId ? { status_id: statusId } : {}),
      },
      { onSuccess: onClose },
    );
  }

  return (
    <Modal
      title={editing ? 'Edit client' : 'New client'}
      subtitle="Clients sit under a Business Unit and carry engagements, commitments and artifacts"
      onClose={onClose}
      onSubmit={submit}
      busy={busy}
      submitLabel="Save client"
      submitIcon={<Check aria-hidden="true" />}
      /*
       * The prototype's `modal wide`: one of the fieldsets is a full-width
       * aliases textarea that a Kimble account list is pasted into whole.
       */
      wide
      layout="plain"
      /*
       * Shown only when the server refused without naming a field, so a
       * field-level problem is never duplicated at the top of the dialog.
       */
      message={generalMessage(
        failure,
        'business_unit_id',
        'code',
        'name',
        'sap_id',
        'aliases',
        'status_id',
      )}
    >
      <fieldset className="mb-4">
        <div className="fieldset-title">Identity</div>
        <div className="grid grid-2">
          <DialogField
            label="Business Unit"
            htmlFor="cl-bu"
            span
            problem={problemFor(failure, 'business_unit_id')}
            hint={
              editing
                ? 'Fixed at creation. A client does not move between units'
                : "Required. Only active units can be chosen, and it decides the client's reporting currency"
            }
          >
            <select
              id="cl-bu"
              value={unitId}
              required
              /*
               * Read-only on edit rather than removed, so the value stays
               * visible where it was chosen.
               */
              disabled={editing}
              onChange={(event) => setUnitId(event.target.value)}
            >
              {editing ? (
                <option value={client.business_unit.id}>
                  {client.business_unit.name}
                </option>
              ) : (
                <>
                  <option value="">Choose a Business Unit</option>
                  {units.map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {unit.name}
                    </option>
                  ))}
                </>
              )}
            </select>
          </DialogField>

          <DialogField
            label="Code"
            htmlFor="cl-code"
            problem={problemFor(failure, 'code')}
            hint={
              editing
                ? 'Immutable. A mutable business identifier is not an identifier'
                : 'Unique within the Business Unit. Upper-case, no spaces'
            }
          >
            <input
              id="cl-code"
              type="text"
              value={code}
              readOnly={editing}
              required
              placeholder="BX"
              onChange={(event) => setCode(event.target.value)}
            />
          </DialogField>

          <DialogField
            label="SAP ID"
            htmlFor="cl-sap"
            problem={problemFor(failure, 'sap_id')}
            hint="Kimble's Account: SAPID, exactly five digits. Normally blank until the engagement feed is loaded"
          >
            <input
              id="cl-sap"
              type="text"
              inputMode="numeric"
              value={sapId}
              placeholder="19710"
              onChange={(event) => setSapId(event.target.value)}
            />
          </DialogField>

          <DialogField
            label="Client name"
            htmlFor="cl-name"
            span
            problem={problemFor(failure, 'name')}
            hint="Unique across every Business Unit, because the Kimble feed and the actuals dumps resolve a client by name alone. Stored exactly as typed, suffix and all"
          >
            <input
              id="cl-name"
              type="text"
              value={name}
              required
              placeholder="The Blackstone Group"
              onChange={(event) => setName(event.target.value)}
            />
          </DialogField>
        </div>
      </fieldset>

      <fieldset className={editing ? undefined : 'mb-4'}>
        <div className="fieldset-title">Feed matching</div>
        <div className="grid grid-2">
          <DialogField
            label="Kimble account aliases"
            htmlFor="cl-alias"
            span
            problem={problemFor(failure, 'aliases')}
            hint='Every spelling listed here resolves to this client when a Kimble dump is staged. Saving replaces the whole set. A spelling that differs only in case needs no entry'
          >
            <textarea
              id="cl-alias"
              value={aliasText}
              placeholder='One Kimble "Account" spelling per line'
              onChange={(event) => setAliasText(event.target.value)}
            />
          </DialogField>
        </div>
      </fieldset>

      {!editing && (
        <fieldset>
          <div className="fieldset-title">Status</div>
          <div className="grid grid-2">
            <DialogField
              label="Status"
              htmlFor="cl-status"
              problem={problemFor(failure, 'status_id')}
              hint="Clients are never deleted -- deactivation is the only removal"
            >
              <select
                id="cl-status"
                value={statusId}
                disabled={catalog.isPending}
                onChange={(event) => setStatusId(event.target.value)}
              >
                {/*
                  The empty option is the DEFAULT, not "none": the server
                  assigns ACTIVE, so the dialog does not have to know its id.
                */}
                <option value="">Active (the default)</option>
                {statuses.map((value) => (
                  <option key={value.id} value={value.id}>
                    {value.name}
                  </option>
                ))}
              </select>
            </DialogField>
          </div>
        </fieldset>
      )}
    </Modal>
  );
}
